import React, { useEffect, useMemo, useState } from 'react'

import { User as UserEntity } from '@/core/api/entities/user'
import { PERMISSIONS } from '@/core/api/services/permissionService'
import { useAccountConfig } from '@/core/hooks/useAccountConfig'
import { usePermission } from '@/core/hooks/usePermission'
import { useUsers } from '@/core/hooks/useUser'
import { useTranslation } from '@/core/i18n/useTranslation'
import { Button } from '@/shared/components/atoms/Button'
import {
	BottomSheet,
	BottomSheetContent,
	BottomSheetTitle,
} from '@/shared/components/molecules/BottomSheet/BottomSheet'
import { Card, CardContent } from '@/shared/components/molecules/Card/Card'
import { useNotificationContext } from '@/shared/context/NotificationProvider'
import { useCurrentAccount } from '@/shared/hooks/useCurrentAccount'

import EditUserModal from './EditUserModal'
import InviteUserModal from './InviteUserModal'
import LoginUserModal from './LoginUserModal'
import RemoveUserModal from './RemoveUserModal'
import { UserRow } from './UserRow'
import { User } from './Users.types'
import { mapUserEntityToUser } from './Users.utils'
import { UsersEmpty } from './UsersEmpty'
import { UsersError } from './UsersError'
import { UsersSkeleton } from './UsersSkeleton'
import { useUserModals } from '../hooks/useUserModals'

/**
 * Users list component
 * Displays a list of users with actions to edit, login as, or remove users
 */
interface UsersProps {
	mobileActionsSignal?: number
}

const Users: React.FC<UsersProps> = ({ mobileActionsSignal = 0 }) => {
	const { t } = useTranslation('common')
	const { showNotification } = useNotificationContext()
	const { hasAnyPermission } = usePermission()
	const canInviteUsers = hasAnyPermission([
		PERMISSIONS.MANAGE_CHILDREN_USERS,
		PERMISSIONS.CREATE_USERS,
		PERMISSIONS.MANAGE_OWN_LEVEL_USERS,
	])
	const canEditUser = hasAnyPermission([
		PERMISSIONS.MANAGE_CHILDREN_USERS,
		PERMISSIONS.MANAGE_OWN_LEVEL_USERS,
		PERMISSIONS.UPDATE_USERS,
	])
	const canDeleteUser = hasAnyPermission([
		PERMISSIONS.MANAGE_CHILDREN_USERS,
		PERMISSIONS.MANAGE_OWN_LEVEL_USERS,
		PERMISSIONS.DESTROY_USERS,
	])
	const [isMobileActionsOpen, setIsMobileActionsOpen] = useState(false)
	const {
		selectedUser,
		isEditModalVisible,
		isRemoveModalVisible,
		isLoginModalVisible,
		isInviteModalVisible,
		isUpdating,
		isDisabling,
		isLoggingIn,
		isInviting,
		openInviteModal,
		openEditModal,
		openRemoveModal,
		closeInviteModal,
		closeEditModal,
		closeRemoveModal,
		closeLoginModal,
		handleEditUser,
		handleRemoveUser,
		handleLoginUser,
		handleInviteUser,
	} = useUserModals(showNotification)

	// Use useAccountConfig to automatically merge selected account ID
	const config = useAccountConfig({
		params: {
			page: 1,
			per_page: 50,
			include: 'account,roles',
		},
	})

	const { data: usersData, isLoading, error } = useUsers({ config })

	const currentUserAccount = useCurrentAccount()

	const users = useMemo<User[]>(() => {
		if (!usersData?.data) return []
		return usersData.data.map((userEntity: UserEntity) =>
			mapUserEntityToUser(userEntity)
		)
	}, [usersData])

	useEffect(() => {
		if (mobileActionsSignal > 0) {
			setIsMobileActionsOpen(true)
		}
	}, [mobileActionsSignal])

	const handleInviteFromMobileActions = () => {
		setIsMobileActionsOpen(false)
		requestAnimationFrame(() => openInviteModal())
	}

	if (isLoading) {
		return <UsersSkeleton />
	}

	if (error) {
		return <UsersError />
	}

	return (
		<div className="space-y-6">
			{canInviteUsers && (
				<div className="hidden lg:flex justify-end">
					<Button
						variant={'outline'}
						onClick={openInviteModal}
						className="rounded-full cursor-pointer"
					>
						{t('account.users.invite_user')}
					</Button>
				</div>
			)}
			<Card>
				<CardContent className="p-0">
					<div className="divide-y">
						{users.length === 0 ? (
							<UsersEmpty />
						) : (
							users.map((user) => (
								<UserRow
									key={user.id}
									user={user}
									currentUserAccountId={currentUserAccount?.id}
									canEdit={canEditUser}
									canDelete={canDeleteUser}
									onEdit={openEditModal}
									onRemove={openRemoveModal}
								/>
							))
						)}
					</div>
				</CardContent>
			</Card>

			{/* Modals */}
			<EditUserModal
				visible={isEditModalVisible}
				user={selectedUser}
				onClose={closeEditModal}
				onSave={handleEditUser}
				isLoading={isUpdating}
			/>

			<RemoveUserModal
				visible={isRemoveModalVisible}
				user={selectedUser as User}
				onClose={closeRemoveModal}
				onConfirm={handleRemoveUser}
				isLoading={isDisabling}
			/>

			<LoginUserModal
				visible={isLoginModalVisible}
				user={selectedUser}
				onClose={closeLoginModal}
				onConfirm={handleLoginUser}
				isLoading={isLoggingIn}
			/>

			<InviteUserModal
				visible={isInviteModalVisible}
				onClose={closeInviteModal}
				onConfirm={handleInviteUser}
				isLoading={isInviting}
			/>

			<BottomSheet
				open={isMobileActionsOpen}
				onOpenChange={setIsMobileActionsOpen}
			>
				<BottomSheetContent
					className="px-4 pb-8 pt-1"
					variant="drawer"
					aria-describedby={undefined}
				>
					<BottomSheetTitle className="sr-only">
						{t('account.navigation.users')}
					</BottomSheetTitle>
					<div className="flex flex-col gap-3">
						{canInviteUsers && (
							<Button
								onClick={handleInviteFromMobileActions}
								className="w-full rounded-full h-14 text-base"
							>
								{t('account.users.modals.invite.invite_user')}
							</Button>
						)}
						<Button
							variant="outline"
							onClick={() => setIsMobileActionsOpen(false)}
							className="w-full rounded-full h-14 text-base"
						>
							{t('actions.cancel')}
						</Button>
					</div>
				</BottomSheetContent>
			</BottomSheet>
		</div>
	)
}

export default Users
export type { User } from './Users.types'
